import { useMemo } from 'react';

import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { useLocation } from 'js/lib/useRouter';

import { isAssignmentGradingInPublicEnabled } from 'bundles/grading-common/utils/epicUtils';
import submissionDataQuery from 'bundles/grading/api/SubmissionDataQuery.graphql';
import submissionDataQueryV2 from 'bundles/grading/api/SubmissionDataQueryV2.graphql';
import type {
  SubmissionDataV2Query,
  SubmissionDataV2QueryVariables,
} from 'bundles/grading/api/__generated__/SubmissionDataQueryV2';
import type {
  CmlContent,
  GradebookEntry,
  GradingStepActionMetadata,
  LastGradingAction,
  SubmissionDataQuery,
  SubmissionDataQueryVariables,
} from 'bundles/grading/api/types';
import { SubmissionManager } from 'bundles/grading/components/staff-graded-assignments/SubmissionManager';
import type { SGASubmitter } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { hasSubmissionFromStatus } from 'bundles/grading/utils/submissionUtils';
import {
  constructSGASubmitter,
  constructSGASubmitterV2,
  getScopedSubmitterIdFromRouteSubmitterId,
} from 'bundles/grading/utils/submitterUtils';

export type SubmissionDataProcessed = {
  submission: SubmissionManager | null;
  /**
   * Submission item's latest grades for the submitter, including grade overrides,
   * and grade visibility.
   * Note: For team submissions, the data is from the first user on the team
   */
  gradebookEntry: GradebookEntry | null;
  graderInstructions: CmlContent | null;
  /**
   * Contains time and user for the latest action made to the rubric parts, grade overrides, or grade visibility
   */
  lastGradingAction: LastGradingAction | null;

  /**
   * This is data needed by the BE to successfully save the response data.
   */
  gradingStepActionMetadata: GradingStepActionMetadata | null;
  /**
   * Score of an assignment calculated by adding up the total number of points received for each
   * assignment part
   */
  rawScore: number | null;
  /**
   * Maximum score set for the assignment, before extra-credit
   */
  maxScore: number | null;

  submitter: SGASubmitter | null;

  /**
   * Name of the current assignment
   */
  itemName: string;
  /**
   * Current group ID. Undefined for Public Session
   */
  groupId?: string;

  isTeamAssignment: boolean;
};

export type UseSubmissionDataResult = {
  loading: boolean;
  data?: SubmissionDataProcessed;
  error?: ApolloError;
  refetch: () => Promise<$TSFixMe>;
};

/**
 * This hook sends a query to fetch a list of submission attempts
 */
const useSubmissionDataQuery = (
  variables: SubmissionDataQueryVariables,
  courseId: string,
  routeSubmitterId: string,
  groupId?: string,
  gradingGroupId?: string
): UseSubmissionDataResult => {
  const { search } = useLocation();

  const useApiV2 = isAssignmentGradingInPublicEnabled();

  const result = useQuery<SubmissionDataQuery, SubmissionDataQueryVariables>(submissionDataQuery, {
    variables,
    skip: useApiV2,
    context: { clientName: 'gatewayGql' },
    onError: (error) => {
      Sentry.captureException('Error fetching Submission Data in Quick Grader', {
        extra: { error, variables: JSON.stringify(variables) },
      });
    },
  });

  // Recalculate `data` object when:
  // - `useQuery` results in updated `response`, which is triggered by changed
  // `submitterIds` in `variables`;
  // - `search` part of the route is changed. This param provides information
  // about current question and rubric, which is crucial for `SubmissionManager`
  //  In this case we need to re-instantiate `submission` object.
  const data: SubmissionDataProcessed | undefined = useMemo(() => {
    if (!result.data || !result.data?.GroupGrading?.queryGroup?.items[0] || result.loading) {
      return undefined;
    }
    const queryGroup = result.data?.GroupGrading?.queryGroup;
    const queryGroupItem = queryGroup?.items[0];

    // gradingTasks can be an empty array - this means submitter is not found
    const gradingTask = queryGroupItem.gradingTasks[0];
    const latestSubmission = gradingTask?.latestSubmission;

    const isAnonymized = Boolean(queryGroupItem.hasAnonymousGrading);
    const isTeamAssignment = Boolean(gradingTask?.submitterId.startsWith('team~'));

    const submission =
      latestSubmission && hasSubmissionFromStatus(latestSubmission.status)
        ? new SubmissionManager(latestSubmission, search)
        : null;

    const submitter = gradingTask
      ? constructSGASubmitter(gradingTask, queryGroup.groupId, isTeamAssignment, isAnonymized)
      : null;

    return {
      gradebookEntry: gradingTask?.gradebookEntry || null,
      graderInstructions: latestSubmission?.graderInstructions || null,
      // since raw and max scores can be undefined/null/0, we need to explicitly check for undefined since undefined/null/0 are all falsy
      rawScore: latestSubmission?.rawScore !== undefined ? latestSubmission.rawScore : null,
      maxScore: latestSubmission?.maxScore !== undefined ? latestSubmission.maxScore : null,
      submission,
      lastGradingAction: latestSubmission?.lastGradingAction || null,
      gradingStepActionMetadata: latestSubmission?.gradingStepActionMetadata || null,
      submitter,
      itemName: queryGroupItem.name,
      groupId: queryGroup.groupId,
      isTeamAssignment,
    };
  }, [result.data, search, result.loading]);

  const variablesV2 = {
    itemId: variables.itemFilter?.itemIds?.[0] || '',
    courseId,
    scopedSubmitterId: getScopedSubmitterIdFromRouteSubmitterId(routeSubmitterId, gradingGroupId),
  };

  const resultV2 = useQuery<SubmissionDataV2Query, SubmissionDataV2QueryVariables>(submissionDataQueryV2, {
    variables: variablesV2,
    skip: !useApiV2,
    context: { clientName: 'gatewayGql' },
    onError: (error) => {
      Sentry.captureException('Error fetching Submission Data V2 in Quick Grader', {
        extra: { error, variables: JSON.stringify(variables) },
      });
    },
  });

  const dataV2: SubmissionDataProcessed | undefined = useMemo(() => {
    if (!resultV2.data || !resultV2.data?.GroupGrading?.queryGradingTask || resultV2.loading) {
      return undefined;
    }

    // gradingTasks can be an empty array - this means submitter is not found
    const gradingTask = resultV2.data?.GroupGrading?.queryGradingTask;
    const latestSubmission = gradingTask?.latestSubmission;

    const isAnonymized = Boolean(gradingTask.item.hasAnonymousGrading);
    const isTeamAssignment = gradingTask?.submitter.__typename === 'GroupGrading_TeamSubmitter';

    const submission =
      latestSubmission && hasSubmissionFromStatus(latestSubmission.status)
        ? new SubmissionManager(latestSubmission, search)
        : null;

    const submitter = gradingTask ? constructSGASubmitterV2(gradingTask, isAnonymized) : null;

    return {
      gradebookEntry: gradingTask?.gradebookEntry || null,
      graderInstructions: latestSubmission?.graderInstructions || null,
      // since raw and max scores can be undefined/null/0, we need to explicitly check for undefined since undefined/null/0 are all falsy
      rawScore: latestSubmission?.rawScore !== undefined ? latestSubmission.rawScore : null,
      maxScore: latestSubmission?.maxScore !== undefined ? latestSubmission.maxScore : null,
      submission,
      lastGradingAction: latestSubmission?.lastGradingAction || null,
      gradingStepActionMetadata: latestSubmission?.gradingStepActionMetadata || null,
      submitter,
      itemName: gradingTask.item.name,
      groupId,
      isTeamAssignment,
    };
  }, [resultV2.data, search, resultV2.loading, groupId]);

  if (useApiV2) {
    return {
      loading: resultV2.loading,
      data: dataV2,
      error: resultV2.error,
      refetch: resultV2.refetch,
    };
  }
  return {
    loading: result.loading,
    data,
    error: result.error,
    refetch: result.refetch,
  };
};

export default useSubmissionDataQuery;
